'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { Stethoscope } from 'lucide-react';
import { uuidv7 } from 'uuidv7';

import { ICD10SingleSelect, useResolveICD10Query, type ICD10CodeT } from '@/entities/labs';
import { useRecordDiagnosisMutation } from '@/entities/diagnoses';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { extractApiErrorDetail } from '@/shared';
import { isRevisionFlow, logger } from '@/shared/lib';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { closeCopilotOverlay } from '@/shared/stores/ui';

import type { DiagnoseWizardPrefill } from '../model/diagnoseWizardPrefill.js';

interface DiagnoseWizardProps {
  userId: string;
  prefill: DiagnoseWizardPrefill | null;
}

/**
 * Form to record a clinician diagnosis (an ICD-10 code + note).
 *
 * Opened when a clinician executes or revises a ``diagnose`` proposed action
 * (proposed by Cortex or the action reviewer), or for manual entry. On submit
 * it records the diagnosis (which writes Care State and fans a FHIR
 * ``Condition`` out to the PHR + Metriport) and finalizes the source proposed
 * action.
 */
export function DiagnoseWizard({ userId, prefill }: DiagnoseWizardProps) {
  const prefilledCode = prefill?.icd10Code ?? null;

  // ``undefined`` means the clinician hasn't touched the field yet, so we fall
  // back to the prefilled code (resolved below). ``null`` means they cleared it.
  const [userSelectedCode, setUserSelectedCode] = useState<ICD10CodeT | null | undefined>(
    undefined
  );
  const [note, setNote] = useState(prefill?.note ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  // Stable per-wizard idempotency key. Generated once so that if recording
  // succeeds but a later step fails, resubmitting reuses the same id and the
  // backend returns the existing diagnosis instead of creating a duplicate.
  const [diagnosisId] = useState(() => uuidv7());
  // Set once recording has succeeded, so a resubmit skips re-recording and only
  // retries the steps that failed (e.g. finalizing the proposed action).
  const recordedRef = useRef(false);

  // Prefills carry a bare code; resolve its authoritative description for display.
  const resolveQuery = useResolveICD10Query(
    prefilledCode ? [prefilledCode] : [],
    prefilledCode !== null
  );

  const selectedCode = useMemo<ICD10CodeT | null>(() => {
    if (userSelectedCode !== undefined) return userSelectedCode;
    if (!prefilledCode) return null;
    const resolved = resolveQuery.data?.codes?.[0];
    return resolved ?? { code: prefilledCode, description: '' };
  }, [userSelectedCode, prefilledCode, resolveQuery.data]);

  const recordDiagnosis = useRecordDiagnosisMutation();
  const mutations = useProposedActionMutations();
  const source = prefill?.sourceProposedAction;

  const isRevision = isRevisionFlow(source);
  const canSubmit = selectedCode !== null && note.trim().length > 0 && !recordDiagnosis.isPending;

  const finalizeProposedAction = useCallback(async () => {
    if (!source) return;
    await mutations.finalizeFromSource(source).catch((err: unknown) => {
      logger.error('diagnoseWizard.finalizeFromSource.failed', {
        externalId: source.externalId,
        err,
      });
    });
  }, [mutations, source]);

  const handleSubmit = useCallback(async () => {
    if (!selectedCode || note.trim().length === 0) {
      setFormError('Select an ICD-10 code and add a note.');
      return;
    }
    setFormError(null);

    // Skip re-recording if a previous submit already recorded the diagnosis and
    // only the follow-up steps failed. Even without this guard the backend is
    // idempotent on ``diagnosisId``, but this avoids a redundant round-trip.
    if (!recordedRef.current) {
      try {
        await recordDiagnosis.mutateAsync({
          userId,
          data: {
            diagnosisId,
            icd10Code: selectedCode.code,
            note: note.trim(),
          },
        });
        recordedRef.current = true;
      } catch (err) {
        logger.error('diagnoseWizard.recordDiagnosis.failed', { userId, err });
        setFormError(extractApiErrorDetail(err) ?? 'Could not record the diagnosis.');
        return;
      }
    }

    await finalizeProposedAction();
    closeCopilotOverlay('diagnoseWizard');
  }, [diagnosisId, finalizeProposedAction, note, recordDiagnosis, selectedCode, userId]);

  const headerSubtitle = useMemo(() => {
    if (isRevision) {
      return 'Adjust the diagnosis code or note, then record it in the patient’s chart.';
    }
    return 'Select an ICD-10 code and add a note. This creates a Condition in the patient’s PHR and submits it.';
  }, [isRevision]);

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-3">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Stethoscope className="size-4 text-muted-foreground" />
          <div className="text-sm font-semibold">Record diagnosis</div>
        </div>
        <p className="text-xs text-muted-foreground">{headerSubtitle}</p>
      </header>

      <section className="space-y-2">
        <Label className="text-xs">Diagnosis code</Label>
        <ICD10SingleSelect
          selected={selectedCode}
          isResolving={resolveQuery.isFetching}
          onSelect={setUserSelectedCode}
          onClear={() => setUserSelectedCode(null)}
        />
      </section>

      <section className="space-y-2">
        <Label htmlFor="diagnose-note" className="text-xs">
          Clinical note
        </Label>
        <Textarea
          id="diagnose-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Summarize the clinical basis for this diagnosis…"
          className="min-h-28 text-sm"
        />
      </section>

      {formError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not record diagnosis</AlertTitle>
          <AlertDescription className="text-xs">{formError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="mt-auto flex justify-end gap-2 pt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => closeCopilotOverlay('diagnoseWizard')}
          className="text-xs"
          disabled={recordDiagnosis.isPending}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          className="text-xs"
        >
          {recordDiagnosis.isPending ? 'Recording…' : 'Record diagnosis'}
        </Button>
      </div>
    </div>
  );
}
